import type {
  ContractAiRiskFindingView,
  ContractAiRiskReviewState,
  ContractAiRiskRunSummary,
} from "@closer/shared";
import {
  CONTRACT_AI_RISK_ADVISORY,
  CONTRACT_AI_RISK_CATEGORY_NAMES,
  CONTRACT_AI_RISK_CERTAINTY_WARNING,
  CONTRACT_AI_RISK_EMPTY_MESSAGE,
  CONTRACT_AI_RISK_ERROR_MESSAGES,
  CONTRACT_AI_RISK_REVIEW_STATE_NAMES,
  CONTRACT_AI_RISK_SEVERITY_NAMES,
  riskBulguAnahtari,
  riskGerekcesiKesinlikIddiaEdiyor,
} from "@closer/shared";
import { Badge, Button, Card, InfoBanner, Select, Skeleton, Textarea, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { AiAdvisoryNotice, AiSourceReference } from "../../components/AiOutputNotice";
import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * RİSKLİ / EKSİK HÜKÜM BULGULARI (CLOS-206 `[189]` · CLOS-1003).
 *
 * ### ⚠ "KESİN HUKUKİ SONUÇ DEĞİLDİR" CÜMLESİ ZORUNLU ve ŞEMADAN
 *
 * Kartın kabul kriteri. `CONTRACT_AI_RISK_ADVISORY` `@closer/shared`'den geliyor: ekran
 * kendi cümlesini yazsaydı bir gün bir yüzeyde unutulur ya da yumuşatılırdı.
 *
 * ### ⚠ BULGU YOKKEN "RİSK YOK" DENMİYOR
 *
 * `CONTRACT_AI_RISK_EMPTY_MESSAGE` boş sonucu anlatırken kesin bir güvence VERMİYOR:
 * *"Bu, sözleşmede risk olmadığı anlamına gelmez."* Kart bunu açıkça yasaklıyor
 * (*"Yetersiz kanıt 'risk yok' ... gibi kesin mesaja dönüşmez"*). `reviewNeeded` listesi
 * de bu yüzden ayrı gösteriliyor: kanıt bulunamayan alanlar bir SONUÇ değil, bir
 * inceleme daveti.
 *
 * ### ⚠ ÖNEM/GÜVEN YALNIZ RENKLE ANLATILMIYOR
 *
 * Kart: *"severity/confidence yalnız renkle anlatılmaz."* Her rozet METİN taşıyor
 * (`CONTRACT_AI_RISK_SEVERITY_NAMES`, `AiSourceReference` içindeki güven adı) — renk
 * yalnız ikinci bir işaret.
 *
 * ### ⚠ ÇELİŞKİ BULGUSUNDA İKİ KAYNAK BİRLİKTE
 *
 * `sources` dizisinin TAMAMI çiziliyor. İlk kaynağı gösterip ötekini gizleseydik, kartın
 * *"iki veya daha fazla kaynak bölüm BİRLİKTE gösterilir"* kriteri sessizce düşerdi ve
 * kullanıcı çelişkiyi doğrulayamazdı.
 *
 * ### ⚠ YOK SAYMA GERİ ALINABİLİR ve METNE DOKUNMAZ
 *
 * "Yok say" bir SİLME değil bir durum; ekranda geri alma düğmesi var ve sunucu tarafı da
 * sözleşme metnine dokunmuyor (kartın kriteri, `aiRisk.ts`'te ölçülüyor).
 */

const KATEGORI_TONU = {
  risky: "danger",
  contradictory: "warning",
  ambiguous: "warning",
  missing: "neutral",
} as const;

type Suzgec = "all" | ContractAiRiskReviewState;

export function ContractAiRiskSection({
  contractId,
  arsivde = false,
  kayitYazma = true,
}: {
  contractId: string;
  arsivde?: boolean;
  kayitYazma?: boolean;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  const { preferences } = usePreferences();
  /** ⚠ Taslak YEREL: kaydedilmemiş çalışma kalıcı listeye hiç girmiyor (analizle aynı). */
  const [taslak, setTaslak] = useState<ContractAiRiskRunSummary | null>(null);
  const [suzgec, setSuzgec] = useState<Suzgec>("all");
  const [notAcik, setNotAcik] = useState<string | null>(null);
  const [notMetni, setNotMetni] = useState("");

  const bulguAnahtari = tenantKey(tenantId, "contract", contractId, "ai-risk-bulgular");
  const bulgular = useQuery({
    queryKey: bulguAnahtari,
    enabled: can("contract.ai_analyze"),
    queryFn: async () =>
      (
        await api<ContractAiRiskFindingView[]>(`/api/contracts/${contractId}/ai-risk-findings`, {
          tenantId,
        })
      ).data,
  });

  /**
   * KALICI ÇALIŞMA LİSTESİ — asıl işi DÜŞEN denemeyi görünür kılmak.
   *
   * Sunucu başarısız çalışmayı kasten listede tutuyor (`aiRisk.ts` ·
   * `listContractAiRiskRuns`): *"denendi, karşılığı alınmadı"* bilgisi kredinin iade
   * edildiğini gösteren tek KALICI iz. Bu uç yazıldığı hâlde hiçbir ekran onu
   * okumuyordu; toast kaybolduktan sonra bölüm "hiç taranmamış" gibi görünüyor ve
   * kullanıcı taramanın neden düştüğünü öğrenemiyordu. Analiz bölümü aynı durumu
   * baştan beri doğru gösteriyor — bu ikisinin ayrışması kullanıcıyı yanıltır.
   */
  const calismaAnahtari = tenantKey(tenantId, "contract", contractId, "ai-risk-calismalar");
  const calismalar = useQuery({
    queryKey: calismaAnahtari,
    enabled: can("contract.ai_analyze"),
    queryFn: async () =>
      (await api<ContractAiRiskRunSummary[]>(`/api/contracts/${contractId}/ai-risks`, { tenantId }))
        .data,
  });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();
  const tara = useMutation({
    mutationFn: async () =>
      (
        await api<ContractAiRiskRunSummary>(`/api/contracts/${contractId}/ai-risks`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({}),
          /** ⚠ Her tıklama yeni anahtar; yalnız ağ tekrar denemesi aynı anahtarı taşır. */
          headers: { "idempotency-key": idempotency.uret() },
        })
      ).data,
    onSuccess: async (calisma) => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "subscription-overview"),
      });
      setTaslak(calisma);
      toast.success("Risk taraması hazır — bulguları inceleyin");
    },
    /**
     * ⚠ HATA DALINDA DA LİSTE TAZELENİYOR. Sunucu düşen denemeyi kalıcı listeye yazıyor;
     * yalnız toast bassaydık o satır ekranda ancak bir sonraki sayfa yüklemesinde belirir,
     * kullanıcı da o ana kadar hiç denememiş gibi bir bölüm görürdü.
     */
    onError: async (err: unknown) => {
      await queryClient.invalidateQueries({ queryKey: calismaAnahtari });
      toast.error(err instanceof Error ? err.message : "Risk taraması çalıştırılamadı");
    },
  });

  const kaydet = useMutation({
    mutationFn: async (runId: string) =>
      (
        await api<ContractAiRiskRunSummary>(`/api/contracts/${contractId}/ai-risks/${runId}/save`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({}),
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bulguAnahtari });
      /** Kaydedilen çalışma artık kalıcı listeye giriyor (`saved`), o liste de tazeleniyor. */
      await queryClient.invalidateQueries({ queryKey: calismaAnahtari });
      setTaslak(null);
      toast.success("Bulgular kaydedildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Bulgular kaydedilemedi"),
  });

  const durumGuncelle = useMutation({
    mutationFn: async (girdi: {
      findingId: string;
      reviewState: ContractAiRiskReviewState;
      note?: string | null;
    }) =>
      (
        await api<ContractAiRiskFindingView>(
          `/api/contracts/${contractId}/ai-risk-findings/${girdi.findingId}`,
          {
            method: "PATCH",
            tenantId,
            body: JSON.stringify({ reviewState: girdi.reviewState, note: girdi.note }),
          },
        )
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: bulguAnahtari });
      setNotAcik(null);
      setNotMetni("");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Bulgu güncellenemedi"),
  });

  /** ⚠ Yetkisiz kullanıcıda bölüm HİÇ çizilmiyor (analiz bölümüyle aynı desen). */
  if (!can("contract.ai_analyze")) return null;

  const liste = bulgular.data ?? [];
  const gorunen = suzgec === "all" ? liste : liste.filter((b) => b.reviewState === suzgec);
  /** Kaydedilmiş ya da düşmüş çalışmalar; taslak burada yok (o yerel ve geçici). */
  const gecmis = calismalar.data ?? [];
  const taslakBulgulari = taslak?.output?.findings ?? [];

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">
          Riskli ve eksik hükümler
        </h2>
        {!arsivde && kayitYazma ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={tara.isPending}
            disabled={tara.isPending}
            onClick={() => tara.mutate()}
          >
            {tara.isPending ? "Taranıyor…" : taslak ? "Yeniden tara" : "Riskleri tara"}
          </Button>
        ) : null}
      </div>

      <AiAdvisoryNotice />
      {/* ⚠ Kartın zorunlu cümlesi — metin şemadan. */}
      <p className="mt-1 text-sm text-fg-muted" data-testid="ai-risk-uyari">
        {CONTRACT_AI_RISK_ADVISORY}
      </p>

      {taslak ? (
        <div className="mt-4 flex flex-col gap-3" data-testid="ai-risk-taslak">
          <div className="flex flex-wrap items-center gap-2 text-sm text-fg-muted">
            <Badge tone="neutral">Kaydedilmedi</Badge>
            <span className="tabular-nums">{taslak.credits} kredi</span>
            {taslak.stale ? <Badge tone="warning">Kaynak metin değişmiş</Badge> : null}
          </div>

          {taslakBulgulari.length === 0 ? (
            <p className="text-sm text-fg-muted">{CONTRACT_AI_RISK_EMPTY_MESSAGE}</p>
          ) : (
            taslakBulgulari.map((bulgu, i) => (
              <BulguKarti
                key={i}
                baslik={bulgu.title}
                kategori={bulgu.category}
                severity={bulgu.severity}
                confidence={bulgu.confidence}
                rationale={bulgu.rationale}
                sources={bulgu.sources}
                mukerrer={
                  taslakBulgulari.filter((b) => riskBulguAnahtari(b) === riskBulguAnahtari(bulgu))
                    .length > 1
                }
              />
            ))
          )}

          {(taslak.output?.reviewNeeded.length ?? 0) > 0 ? (
            <InfoBanner tone="warning">
              <div>
                <p className="font-medium">Kanıt yetersiz — elle incelenmeli</p>
                <ul className="mt-1 list-disc pl-5 text-sm">
                  {taslak.output!.reviewNeeded.map((satir, i) => (
                    <li key={i}>{satir}</li>
                  ))}
                </ul>
              </div>
            </InfoBanner>
          ) : null}

          {kayitYazma ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                loading={kaydet.isPending}
                disabled={kaydet.isPending}
                onClick={() => kaydet.mutate(taslak.id)}
              >
                {kaydet.isPending ? "Kaydediliyor…" : "Bulguları kaydet"}
              </Button>
              {/* ⚠ "Vazgeç" bir API çağrısı değil: kredi zaten kesin olarak tüketildi. */}
              <Button type="button" size="sm" variant="ghost" onClick={() => setTaslak(null)}>
                Vazgeç
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-4">
        {bulgular.isPending ? (
          <Skeleton lines={3} />
        ) : bulgular.error ? (
          <StateView
            state={resolveErrorState(bulgular.error, {
              idempotent: true,
              overrides: {
                error: {
                  title: "Risk bulguları yüklenemedi",
                  description: "Bölüm şu an getirilemedi.",
                },
              },
            })}
            onRetry={() => void bulgular.refetch()}
          />
        ) : liste.length === 0 ? (
          <p className="text-sm text-fg-muted">
            {taslak
              ? "Kaydedilmiş bulgu yok."
              : /**
                 * ⚠ "Hiç taranmadı" cümlesi YALNIZ gerçekten hiç çalışma yokken. Düşen bir
                 * deneme varken bunu demek, kullanıcının harcadığı emeği ve gördüğü hatayı
                 * yok saymak olurdu — geçmiş aşağıda sebebiyle duruyor.
                 */
                gecmis.length > 0
                ? "Kaydedilmiş bulgu yok; geçmiş denemeler aşağıda."
                : "Bu sözleşme için henüz risk taraması kaydedilmedi."}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-sm text-fg-muted" htmlFor="ai-risk-suzgec">
                Durum
              </label>
              <Select
                id="ai-risk-suzgec"
                className="w-48"
                value={suzgec}
                onChange={(e) => setSuzgec(e.target.value as Suzgec)}
              >
                <option value="all">Tümü</option>
                <option value="open">{CONTRACT_AI_RISK_REVIEW_STATE_NAMES.open}</option>
                <option value="reviewed">{CONTRACT_AI_RISK_REVIEW_STATE_NAMES.reviewed}</option>
                <option value="dismissed">{CONTRACT_AI_RISK_REVIEW_STATE_NAMES.dismissed}</option>
              </Select>
              <span className="text-sm text-fg-muted">
                {gorunen.length} / {liste.length}
              </span>
            </div>

            {gorunen.length === 0 ? (
              <p className="text-sm text-fg-muted">Bu süzgeçle eşleşen bulgu yok.</p>
            ) : (
              gorunen.map((bulgu) => (
                <div key={bulgu.id} data-testid={`ai-risk-bulgu-${bulgu.id}`}>
                  <BulguKarti
                    baslik={bulgu.title}
                    kategori={bulgu.category}
                    severity={bulgu.severity}
                    confidence={bulgu.confidence}
                    rationale={bulgu.rationale}
                    sources={bulgu.sources}
                    mukerrer={liste.filter((b) => b.dedupKey === bulgu.dedupKey).length > 1}
                    ustBilgi={
                      <>
                        <Badge tone={bulgu.reviewState === "dismissed" ? "neutral" : "success"}>
                          {CONTRACT_AI_RISK_REVIEW_STATE_NAMES[bulgu.reviewState]}
                        </Badge>
                        {/* ⚠ Bayat bulgu AYRI işaretleniyor: kaynak versiyon artık güncel değil. */}
                        {bulgu.stale ? <Badge tone="warning">Kaynak metin değişmiş</Badge> : null}
                        {bulgu.reviewedByName ? (
                          <span className="text-xs text-fg-muted">
                            {bulgu.reviewedByName} ·{" "}
                            {bulgu.reviewedAt
                              ? formatDate(bulgu.reviewedAt, preferences.locale)
                              : ""}
                          </span>
                        ) : null}
                      </>
                    }
                    alt={
                      <>
                        {bulgu.note ? (
                          <p className="mt-2 rounded-md bg-canvas p-2 text-sm text-fg">
                            {bulgu.note}
                          </p>
                        ) : null}
                        {kayitYazma ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {bulgu.reviewState !== "reviewed" ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                disabled={durumGuncelle.isPending}
                                onClick={() =>
                                  durumGuncelle.mutate({
                                    findingId: bulgu.id,
                                    reviewState: "reviewed",
                                  })
                                }
                              >
                                İncelendi
                              </Button>
                            ) : null}
                            {bulgu.reviewState !== "dismissed" ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                disabled={durumGuncelle.isPending}
                                onClick={() =>
                                  durumGuncelle.mutate({
                                    findingId: bulgu.id,
                                    reviewState: "dismissed",
                                  })
                                }
                              >
                                Yok say
                              </Button>
                            ) : (
                              /* ⚠ GERİ ALMA: yok sayma bir silme değil (kartın kriteri). */
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                disabled={durumGuncelle.isPending}
                                onClick={() =>
                                  durumGuncelle.mutate({ findingId: bulgu.id, reviewState: "open" })
                                }
                              >
                                Geri al
                              </Button>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setNotAcik(notAcik === bulgu.id ? null : bulgu.id);
                                setNotMetni(bulgu.note ?? "");
                              }}
                            >
                              {bulgu.note ? "Notu düzenle" : "Not ekle"}
                            </Button>
                          </div>
                        ) : null}
                        {notAcik === bulgu.id ? (
                          <div className="mt-2 flex flex-col gap-2">
                            <Textarea
                              rows={2}
                              aria-label="Bulgu notu"
                              value={notMetni}
                              onChange={(e) => setNotMetni(e.target.value)}
                            />
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                size="sm"
                                disabled={durumGuncelle.isPending}
                                onClick={() =>
                                  durumGuncelle.mutate({
                                    findingId: bulgu.id,
                                    reviewState: bulgu.reviewState,
                                    /** ⚠ Boş not `null`: notu SİLMEK ile boş bırakmak aynı şey. */
                                    note: notMetni.trim() === "" ? null : notMetni.trim(),
                                  })
                                }
                              >
                                Kaydet
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => setNotAcik(null)}
                              >
                                Vazgeç
                              </Button>
                            </div>
                          </div>
                        ) : null}
                      </>
                    }
                  />
                </div>
              ))
            )}
          </div>
        )}

        {/*
          GEÇMİŞ TARAMALAR — analiz bölümüyle AYNI sunum, bilinçli olarak.
          Kullanıcı iki yüzeyde aynı şeyi yaptığında (çalıştır, düştü) aynı yerde aynı
          cevabı görmeli. Düşen satır sebebini ve "0 kredi"yi taşıyor: ikincisi kredinin
          iade edildiğinin okunabilir kanıtı.
        */}
        {gecmis.length > 0 ? (
          <div className="mt-4 flex flex-col gap-2">
            <h3 className="text-sm font-medium text-fg-strong">Geçmiş taramalar</h3>
            {gecmis.map((calisma) => (
              <div
                key={calisma.id}
                data-testid={`ai-risk-calisma-${calisma.id}`}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-border-subtle p-3"
              >
                <Badge tone={calisma.status === "completed" ? "success" : "danger"}>
                  {calisma.status === "completed" ? "Tamamlandı" : "Başarısız"}
                </Badge>
                <span className="text-sm text-fg">
                  {formatDate(calisma.createdAt, preferences.locale)} · {calisma.createdByName}
                </span>
                <span className="ml-auto text-sm tabular-nums text-fg-muted">
                  {calisma.credits} kredi
                </span>
                {calisma.errorCode ? (
                  <p className="w-full text-sm text-danger">
                    {CONTRACT_AI_RISK_ERROR_MESSAGES[calisma.errorCode]}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function BulguKarti({
  baslik,
  kategori,
  severity,
  confidence,
  rationale,
  sources,
  ustBilgi,
  alt,
  mukerrer = false,
}: {
  baslik: string;
  kategori: keyof typeof KATEGORI_TONU;
  severity: keyof typeof CONTRACT_AI_RISK_SEVERITY_NAMES;
  confidence: "high" | "medium" | "low";
  rationale: string;
  sources: { clause: string; quote: string }[];
  ustBilgi?: React.ReactNode;
  alt?: React.ReactNode;
  /** ⚠ Aynı kaynak sorunun daha önce de raporlandığı (CLOS-1084 dedup) işareti. */
  mukerrer?: boolean;
}) {
  return (
    <article className="rounded-lg border border-border-subtle p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={KATEGORI_TONU[kategori]}>{CONTRACT_AI_RISK_CATEGORY_NAMES[kategori]}</Badge>
        {/* ⚠ Önem METİNLE: yalnız renkle anlatmak kartın yasakladığı şey. */}
        <Badge tone="neutral">{CONTRACT_AI_RISK_SEVERITY_NAMES[severity]}</Badge>
        {ustBilgi}
      </div>
      <h3 className="mt-2 text-sm font-semibold text-fg-heading">{baslik}</h3>
      <p className="mt-1 text-sm text-fg">{rationale}</p>

      {/*
        ⚠ KESİN İFADE İŞARETİ (CLOS-1084): modelin cümlesi silinmiyor ya da düzeltilmiyor —
        yanına ne olduğu yazılıyor. Sessizce düzeltmek, AI'ın ne dediğini kullanıcıdan
        gizlemek olurdu; reddetmek ise kullanıcının kredisini bir kelime seçimi yüzünden
        yakardı.
      */}
      {riskGerekcesiKesinlikIddiaEdiyor(rationale) ? (
        <p className="mt-1 text-xs text-warning" data-testid="ai-risk-kesinlik">
          {CONTRACT_AI_RISK_CERTAINTY_WARNING}
        </p>
      ) : null}

      {/*
        ⚠ MÜKERRER İŞARETİ: aynı kaynak sorunu birden çok bulguya bölündüğünde kullanıcı
        "bulgu fırtınası" görüyor. Satırları GİZLEMİYORUZ (hangisinin gerçek olduğunu ekran
        bilemez), yalnız aynı sorunun tekrarı olduğunu söylüyoruz.
      */}
      {mukerrer ? (
        <p className="mt-1 text-xs text-fg-muted" data-testid="ai-risk-mukerrer">
          Aynı kaynak sorununa ilişkin başka bir bulgu daha var.
        </p>
      ) : null}

      {/*
        ⚠ KAYNAKLARIN TAMAMI: çelişki bulgusunda iki bölüm BİRLİKTE görünmeli (kartın
        kriteri). Kaynak yoksa (eksik hüküm) `AiSourceReference` "Kaynaklanamadı" yazıyor —
        satır gizlenmiyor, çünkü kaynaksızlık da bir bilgi.
      */}
      {sources.length === 0 ? (
        <AiSourceReference source={{ clause: "", quote: "" }} confidence={confidence} />
      ) : (
        sources.map((kaynak, i) => (
          <AiSourceReference key={i} source={kaynak} confidence={confidence} />
        ))
      )}
      {alt}
    </article>
  );
}
